import { Linkedin, Instagram, Mail, ArrowUp, Heart, MapPin } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cvData } from '../../data/cv';

const PHOTO_URL =
  'https://bardobggjtozmuyyadhf.supabase.co/storage/v1/object/public/asfdd//files_7358450-2026-02-17T22-51-29-101Z-Blue_and_White_Simple_Man_LinkedIn_Profile_Picture_20251118_075129_0000.png';

export const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navLinks = [
    { href: '#about', label: 'About', anchor: true },
    { href: '#experience', label: 'Experience', anchor: true },
    { href: '#education', label: 'Education', anchor: true },
    { href: '#skills', label: 'Skills', anchor: true },
    { href: '/projects', label: 'Projects', anchor: false },
    { href: '/blog', label: 'Blog', anchor: false },
    { href: '/videos', label: 'Videos', anchor: false },
    { href: '/contact', label: 'Contact', anchor: false },
  ];

  const scrollTo = (href: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      window.setTimeout(() => document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' }), 120);
      return;
    }
    document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="relative overflow-hidden" style={{ background: 'linear-gradient(180deg, hsl(220 25% 8%) 0%, hsl(220 28% 6%) 100%)' }}>
      {/* Premium decorative elements */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-[0.03] blur-[100px] pointer-events-none" style={{ background: 'hsl(var(--primary))' }} />
      <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full opacity-[0.02] blur-[80px] pointer-events-none" style={{ background: 'hsl(var(--accent))' }} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">

        {/* Main footer body */}
        <div className="pt-12 sm:pt-16 pb-10 sm:pb-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-10 sm:gap-12 items-start border-b border-white/[0.06]">

          {/* Brand column */}
          <div className="flex flex-col gap-6">
            <Link to="/" className="flex items-center gap-4 group w-fit">
              <div className="relative flex-shrink-0">
                <div className="w-14 h-14 rounded-full ring-2 ring-primary/40 group-hover:ring-primary/70 overflow-hidden shadow-lg transition-all duration-300">
                  <img
                    src={PHOTO_URL}
                    alt="Dr. Yesh Jitesh"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <span aria-hidden="true" className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-gradient-to-br from-green-400 to-green-500 rounded-full border-2 border-[hsl(220_25%_8%)]" />
              </div>

              <div>
                <p
                  className="text-base font-bold text-white leading-tight group-hover:text-primary transition-colors duration-300"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Dr. Yesh Jitesh
                </p>
                <p className="text-xs text-white/50 mt-1 font-medium">{cvData.personal.title}</p>
              </div>
            </Link>

            <p className="text-sm text-white/60 leading-relaxed max-w-xs">
              Clinician and healthcare innovator combining hands-on patient care with technology-driven solutions. Founder of RotaCare.uk and IMG2NHS.com
            </p>

            <div className="flex items-center gap-2.5 text-xs text-white/55">
              <MapPin size={12} className="text-primary/60 flex-shrink-0" />
              {cvData.personal.location}
            </div>
          </div>

          {/* Quick links */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/50 mb-6">Quick Links</p>
            <ul className="space-y-3">
              {navLinks.map((link) => (
                <li key={link.href}>
                  {link.anchor ? (
                    <button
                      onClick={() => scrollTo(link.href)}
                      className="text-sm text-white/65 hover:text-primary transition-colors duration-200 font-medium"
                    >
                      {link.label}
                    </button>
                  ) : (
                    <Link
                      to={link.href}
                      className="text-sm text-white/65 hover:text-primary transition-colors duration-200 font-medium"
                    >
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact column */}
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/50 mb-6">Get in Touch</p>
            <div className="space-y-3.5 mb-8">
              <a
                href={cvData.personal.linkedin}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3.5 text-sm text-white/65 hover:text-primary transition-colors duration-200 group min-h-[44px]"
              >
                <span className="w-11 h-11 rounded-xl bg-white/5 group-hover:bg-primary/20 flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:shadow-lg">
                  <Linkedin size={16} className="text-white/70 group-hover:text-primary transition-colors" />
                </span>
                LinkedIn Profile
              </a>
              <a
                href={cvData.personal.instagram}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3.5 text-sm text-white/65 hover:text-primary transition-colors duration-200 group min-h-[44px]"
              >
                <span className="w-11 h-11 rounded-xl bg-white/5 group-hover:bg-primary/20 flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:shadow-lg">
                  <Instagram size={16} className="text-white/70 group-hover:text-primary transition-colors" />
                </span>
                Instagram Profile
              </a>
              <a
                href={`mailto:${cvData.personal.email}`}
                className="flex items-center gap-3.5 text-sm text-white/65 hover:text-primary transition-colors duration-200 group min-h-[44px]"
              >
                <span className="w-11 h-11 rounded-xl bg-white/5 group-hover:bg-primary/20 flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:shadow-lg">
                  <Mail size={16} className="text-white/70 group-hover:text-primary transition-colors" />
                </span>
                {cvData.personal.email}
              </a>
            </div>

            <Link
              to="/contact"
              className="w-full block text-center py-3 rounded-xl text-sm font-semibold border border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300 hover:shadow-hero"
            >
              Get in Touch
            </Link>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/45 flex items-center gap-1.5">
            © {new Date().getFullYear()} Dr. Yesh Jitesh. Built with
            <Heart size={10} className="text-red-400/80 fill-red-400/80" />
            for better healthcare.
          </p>

          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            aria-label="Back to top"
            className="flex items-center gap-2.5 text-xs text-white/60 hover:text-primary transition-colors duration-200 group min-h-[44px] px-2"
          >
            Back to top
            <span className="w-10 h-10 rounded-xl bg-white/5 group-hover:bg-primary/20 flex items-center justify-center transition-all duration-300 group-hover:shadow-lg">
              <ArrowUp size={14} className="text-white/70 group-hover:text-primary transition-colors" />
            </span>
          </button>
        </div>
      </div>
    </footer>
  );
};
